<script>
  import Icon from "@iconify/svelte";
  import basebloxLogo from "../baseblox.svg";
  import { t } from "./lib/i18n.js";
  import LanguageSwitcher from "./components/LanguageSwitcher.svelte";

  export let apiBase = "";
  export let jsonHeaders = {};
  export let error = "";
  export let locale = "en";
  export let storeEngine = "";
  export let onLocaleChange = () => {};
  export let onSave = () => {};

  let inputKey = "";
  let loading = false;
  let localErr = "";

  async function handleSubmit(e) {
    e.preventDefault();
    localErr = "";

    if (!inputKey.trim()) {
      localErr = t(locale, "login_api_key_required");
      return;
    }

    loading = true;

    try {
      const res = await fetch(`${apiBase}/setup`, {
        method: "GET",
        cache: "no-store",
        headers: {
          ...jsonHeaders,
          Authorization: `Bearer ${inputKey.trim()}`
        }
      });

      if (res.status === 401) {
        localErr = t(locale, "login_invalid_key");
        return;
      }

      if (!res.ok) {
        localErr = t(locale, "login_server_error", { status: res.status });
        return;
      }

      onSave(inputKey.trim());
    } catch (e) {
      localErr = e?.message || t(locale, "login_connection_failed");
    } finally {
      loading = false;
    }
  }

  $: displayErr = localErr || error;
</script>

<div class="ui-login-shell min-h-screen flex items-center justify-center px-4 py-10">
  <div class="w-full max-w-5xl ui-login-grid items-stretch">
    <aside class="order-2 flex flex-col justify-center gap-6 p-6 sm:p-8 lg:order-1 lg:pr-10">
      <div class="space-y-4">
        <h2 class="flex items-center gap-3 text-2xl font-light leading-snug text-neutral-100 sm:text-3xl lowercase">
          <img src={basebloxLogo} alt="BaseBlox" class="h-10 w-10 shrink-0 rounded-xl" />
          {t(locale, "login_acdev_headline")}
        </h2>
        <p class="text-sm leading-relaxed text-neutral-400">
          {t(locale, "login_acdev_description")}
        </p>
        <a
          href={`https://${t(locale, "login_acdev_link")}`}
          target="_blank"
          rel="noopener noreferrer"
          class="ui-login-link inline-flex items-center gap-1.5 text-sm"
        >
          {t(locale, "login_acdev_link")}
          <Icon icon="mdi:arrow-top-right" class="h-4 w-4" />
        </a>
      </div>
      <div class="text-sm text-neutral-500">
        {t(locale, "login_acdev_licensing")} -
        <a href={`mailto:${t(locale, "login_acdev_contact")}`} class="ui-login-link">
          {t(locale, "login_acdev_contact")}
        </a>
      </div>
    </aside>

    <div class="ui-login-separator order-2 hidden self-stretch lg:block" aria-hidden="true"></div>
    <section class="ui-card ui-login-card order-1 flex flex-col p-8 sm:p-10 lg:order-3">
      <div class="mb-6 flex items-center justify-end">
        <LanguageSwitcher bind:locale onchange={onLocaleChange} />
      </div>

      <div class="mb-8 flex flex-col items-center gap-3 text-center">
        <div class="ui-login-badge flex h-14 w-14 items-center justify-center">
          <Icon icon="streamline-ultimate:warehouse-cart-packages-2-bold" width="32" height="32" />
        </div>
        <div class="text-3xl font-light tracking-wide text-neutral-100 lowercase">
          {t(locale, "app_name")}
        </div>
        <div class="text-sm text-neutral-400">{storeEngine || t(locale, "login_fallback_subtitle")}</div>
      </div>

      <form onsubmit={handleSubmit} class="mx-auto w-full max-w-sm space-y-3">
        {#if displayErr}
          <div class="rounded-lg border border-rose-800/60 bg-rose-950/40 px-4 py-3 text-sm text-rose-200">
            {displayErr}
          </div>
        {/if}

        <input
          type="password"
          bind:value={inputKey}
          required
          placeholder={t(locale, "login_enter_key")}
          class="ui-login-input px-3 py-2.5"
          disabled={loading}
          autocomplete="current-password"
        />

        <button
          type="submit"
          class="ui-login-button inline-flex h-11 w-full items-center justify-center gap-2 px-4 py-2.5 text-sm font-normal lowercase"
          disabled={loading}
        >
          {#if loading}
            <Icon icon="mdi:loading" class="h-4 w-4 animate-spin" />
          {/if}
          {loading ? t(locale, "login_authenticating") : t(locale, "login_button")}
        </button>
      </form>
    </section>
  </div>
</div>
